import { createBeacon, getBeacon, updateBeacon } from "api/beacons.api";
import { ErrorResponseDto } from "api/config/ErrorResponseDto";
import { FieldState, FormState } from "formstate";
import { isEmpty } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "stores/RootStore";
import { BeaconDto } from "types/beacon";
import { isMACAddress, isUUID, MinimumLengthValidation, required, RequiredValidationError } from "utils/formstate";
import { isDeepEqual } from "utils/utils";

type BeaconForm = FormState<{
  beacon_id: FieldState<string>,
  beacon_type: FieldState<string>,
  notes: FieldState<string>,
  mac_address: FieldState<string>,

  hasEddystone: FieldState<boolean>,
  hasIbeacon: FieldState<boolean>,

  namespace: FieldState<string>,
  instance: FieldState<string>,
  proximity_uuid: FieldState<string>,

  major: FieldState<number | undefined>,
  minor: FieldState<number | undefined>,
}>

export class BeaconStore {
  root: RootStore;
  dataLoading = false;
  error: ErrorResponseDto | null = null
  currentBeacon: BeaconDto = new BeaconDto()
  beaconForm = this.createForm();
  saveLoading: boolean = false;
  ischangesSaved: boolean = false

  constructor(root: RootStore) {
    this.root = root;

    makeObservable(this, {
      dataLoading: observable,
      currentBeacon: observable,
      error: observable,
      saveLoading: observable,
      beaconForm: observable,
      ischangesSaved: observable,

      reset: action.bound,
      loadBeacon: action.bound,
      SaveBeacon: action.bound,

      isAnyChange: computed,
      isFormEmpty: computed,
    });

    this.beaconForm.validate();
  }

  createForm(): BeaconForm {

    const mac_address = this.currentBeacon?.data.broadcast_profiles?.eddystone_uid?.mac_address
      || this.currentBeacon?.data.broadcast_profiles?.ibeacon?.mac_address || ""

    return new FormState({
      beacon_id: new FieldState(this.currentBeacon?.data.beacon_id),
      beacon_type: new FieldState(this.currentBeacon?.data.beacon_type).validators(required, MinimumLengthValidation),
      notes: new FieldState(this.currentBeacon?.data.notes),
      mac_address: new FieldState(mac_address).validators(isMACAddress),

      hasEddystone: new FieldState(!isEmpty(this.currentBeacon?.data.broadcast_profiles?.eddystone_uid)),
      hasIbeacon: new FieldState(!isEmpty(this.currentBeacon?.data.broadcast_profiles?.ibeacon)),

      namespace: new FieldState(this.currentBeacon?.data.broadcast_profiles?.eddystone_uid?.namespace || "").validators(
        (value) => this.beaconForm.$.hasEddystone.value && value === "" && RequiredValidationError
      ),
      instance: new FieldState(this.currentBeacon?.data.broadcast_profiles?.eddystone_uid?.instance || "").validators(
        (value) => this.beaconForm.$.hasEddystone.value && value === "" && RequiredValidationError
      ),

      proximity_uuid: new FieldState(this.currentBeacon?.data.broadcast_profiles?.ibeacon?.proximity_uuid || "").validators(
        isUUID, value => this.beaconForm.$.hasIbeacon.value && value === "" && RequiredValidationError),
      major: new FieldState(this.currentBeacon?.data.broadcast_profiles?.ibeacon?.major).validators(
        value => this.beaconForm.$.hasIbeacon.value && value === undefined && RequiredValidationError),
      minor: new FieldState(this.currentBeacon?.data.broadcast_profiles?.ibeacon?.minor).validators(
        value => this.beaconForm.$.hasIbeacon.value && value === undefined && RequiredValidationError),
    });
  }

  loadBeacon(beacon_id: string) {
    this.dataLoading = true
    this.error = null

    return getBeacon(beacon_id)
      .then((res) => {
        this.currentBeacon = res
        this.beaconForm = this.createForm();
        this.dataLoading = false
      })
      .catch((error: ErrorResponseDto) => this.error = error)
  }

  fillForm(data: BeaconDto | null) {
    if (data) {
      this.currentBeacon = data;
    }
    this.beaconForm = this.createForm();
    this.beaconForm.validate();
  }

  reset() {
    this.currentBeacon = new BeaconDto();
    this.beaconForm = this.createForm();
    this.beaconForm.validate();
    this.dataLoading = false
    this.saveLoading = false
    this.ischangesSaved = false
    this.error = null
  }

  BuildBeaconDto() {
    const obj = new BeaconDto();

    obj.data.import_info = this.currentBeacon.data.import_info
    obj.data.beacon_id = this.beaconForm.$.beacon_id.value
    obj.data.beacon_type = this.beaconForm.$.beacon_type.value
    obj.data.notes = this.beaconForm.$.notes.value
    obj.data.broadcast_profiles = {
      eddystone_uid: null,
      ibeacon: null
    }

    if (this.currentBeacon) {
      obj.revision = this.currentBeacon.revision
    }
    if (this.beaconForm.$.hasEddystone.value) {
      obj.data.broadcast_profiles.eddystone_uid = {
        type: "eddystone_uid",
        namespace: this.beaconForm.$.namespace.value,
        instance: this.beaconForm.$.instance.value,
        mac_address: this.beaconForm.$.mac_address.value || null,
        tx_power: this.currentBeacon.data.broadcast_profiles.eddystone_uid?.tx_power || null,
        calibrated_rssi: this.currentBeacon.data.broadcast_profiles.eddystone_uid?.calibrated_rssi || null,
        secure_namespace: this.currentBeacon.data.broadcast_profiles.eddystone_uid?.secure_namespace || null,
      }
    }
    if (this.beaconForm.$.hasIbeacon.value) {
      obj.data.broadcast_profiles.ibeacon = {
        type: "ibeacon",
        proximity_uuid: this.beaconForm.$.proximity_uuid.value,
        major: this.beaconForm.$.major.value || 0,
        minor: this.beaconForm.$.minor.value || 0,
        mac_address: this.beaconForm.$.mac_address.value || null,
        tx_power: this.currentBeacon.data.broadcast_profiles.ibeacon?.tx_power || null,
        calibrated_rssi: this.currentBeacon.data.broadcast_profiles.ibeacon?.calibrated_rssi || null,
        secure_proximity_uuid: this.currentBeacon.data.broadcast_profiles.ibeacon?.secure_proximity_uuid || null,
      }
    }
    return obj
  }

  async SaveBeacon() {
    this.saveLoading = true;
    const obj = this.BuildBeaconDto()

    const promise = this.currentBeacon.data.beacon_id !== ""
      ? updateBeacon(obj)
      : createBeacon(obj.data)
    return promise
      .then(() => this.ischangesSaved = true)
      .catch((error) => Promise.reject(error))
      .finally(() => this.saveLoading = false)
  }

  get isAnyChange() {
    const srcObj = this.currentBeacon;
    const finalObj = this.BuildBeaconDto();
    const debug = false;

    //............... always return true for create mode .....................
    if (!this.currentBeacon) {
      return false;
    }
    if (!isDeepEqual(srcObj, finalObj,
      ["revision", "last_update_timestamp", "creation_timestamp", "_version", "assigned_asset", "location", "mobility_state",
        "battery", "last_location_update_timestamp", "assigned_asset_id",
        "import_info", "calibrated_rssis", "calibrated_rssi", "secure_namespace", "secure_proximity_uuid"], debug)) {
      return true;
    }
    return false;
  }

  get isFormEmpty() {
    return this.beaconForm.$.beacon_id.value === "" &&
      this.beaconForm.$.beacon_type.value === ""
  }
}